/**
 * Robinhood Chain — canonical network parameters.
 *
 * Verified against mainnet (2026-08): eth_chainId → 0x1237, nitro/v3.11.3.
 * The local development fork runs the SAME chain id with the SAME contract
 * addresses, so nothing in the application branches on "is this a testnet".
 * The only difference between local and production is the RPC URL.
 */

export const ROBINHOOD_CHAIN_ID = 4663 as const;
export const ROBINHOOD_CHAIN_ID_HEX = '0x1237' as const;

export const ROBINHOOD_MAINNET_RPC = 'https://rpc.mainnet.chain.robinhood.com';
export const ROBINHOOD_EXPLORER = 'https://robinhoodchain.blockscout.com';

/** Contracts that already exist on Robinhood Chain mainnet. */
export const MAINNET_ADDRESSES = {
  weth: '0x0Bd7D308f8E1639FAb988df18A8011f41EAcAD73',
  univ3Factory: '0x1f7d7550B1b028f7571E69A784071F0205FD2EfA',
  univ3PositionManager: '0x73991a25C818Bf1f1128dEAaB1492D45638DE0D3',
  univ3SwapRouter: '0xCaf681a66D020601342297493863E78C959E5cb2',
  univ3QuoterV2: '0x33e885eD0Ec9bF04EcfB19341582aADCb4c8A9E7',
  /** pons.family launchpad — active factory and permanent LP locker. */
  ponsFactory: '0xA5aAb3F0c6EeadF30Ef1D3Eb997108E976351feB',
  ponsLocker: '0x736D76699C26D0d966744cAe304C000d471f7F35',
} as const;

/** pons.family launch parameters, mirrored so the app can display them. */
export const PONS_PARAMS = {
  /** Uniswap V3 fee tier used by the active pons factory: 1%. */
  poolFee: 10_000,
  /** Fixed supply minted by the launchpad: 1e9 tokens. */
  fixedSupply: 1_000_000_000n,
  /** WETH in the pool at which a launch is marked "graduated". */
  graduationThresholdEth: 4.2,
  /** Pool fee split on the active factory. */
  creatorFeeBps: 7_000,
  protocolFeeBps: 3_000,
  launchFeeEth: 0.0005,
} as const;

/** viem-compatible chain definition. */
export function robinhoodChain(rpcUrl: string) {
  return {
    id: ROBINHOOD_CHAIN_ID,
    name: 'Robinhood Chain',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpcUrls: {
      default: { http: [rpcUrl] },
      public: { http: [rpcUrl] },
    },
    blockExplorers: {
      default: { name: 'Blockscout', url: ROBINHOOD_EXPLORER },
    },
  } as const;
}

/** Addresses written by scripts/chain-deploy.sh into deployments/4663.json. */
export interface LegateDeployment {
  chainId: number;
  /** Block the fork was pinned at, or the live deployment block. */
  blockNumber: string;
  deployedAt: string;
  token: string;
  staking: string;
  creditSink: string;
  treasury: string;
  vesting: string;
  twapOracle: string;
  /** Uniswap V3 LEGATE/WETH pool created at deploy time. */
  pool: string;
  weth: string;
  univ3Factory: string;
  univ3PositionManager: string;
}
